import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import { OutlinedInput } from '@material-ui/core';
import { getColor } from '../../../utils/colorGenerator';
import graphKeysPropTypes from '../../../proptypes/graphKeysPropTypes';

const styles = theme => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    formControl: {
        minWidth: 240,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: theme.spacing(0.25),
    },
    forcedLabelBackground: {
        background: theme.palette.background.paper,
        padding: '0 2px',
    },
});

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: '70vh',
            width: 250,
            minWidth: 'inherit',
        },
    },
};

const MultiSelect = ({
    classes,
    allValues,
    selectedValues,
    handleSelect,
    icon,
    label,
    placeholder,
    fullWidth,
    labelBackgroundColor,
}) => {
    const selectedIds = selectedValues.map(value => value.id);

    const isSelected = id => selectedIds.includes(id);

    const handleChange = (event) => {
        const newSelectedValues = event.target.value.map(
            selectedId => allValues.find(
                value => value.id === selectedId,
            ),
        );
        handleSelect(newSelectedValues);
    };

    const handleDelete = idToDelete => () => {
        const newSelectedValues = selectedValues.filter(value => value.id !== idToDelete);
        handleSelect(newSelectedValues);
    };

    return (
        <div className={classes.root}>
            <FormControl
                className={classes.formControl}
                margin="dense"
                variant="outlined"
                fullWidth={fullWidth}
            >
                <InputLabel
                    shrink
                    htmlFor="select-multiple-checkbox"
                    className={classes.forcedLabelBackground}
                    {...(labelBackgroundColor
                    && { style: { backgroundColor: labelBackgroundColor } })}
                >
                    {label}
                </InputLabel>
                <Select
                    multiple
                    displayEmpty
                    value={selectedIds}
                    margin="dense"
                    onChange={handleChange}
                    input={
                        (
                            <OutlinedInput
                                labelWidth={0}
                                notched
                                id="select-multiple-checkbox"
                            />
                        )
                    }
                    renderValue={(selected) => {
                        if (selected.length === 0 && placeholder) {
                            return <em>{placeholder}</em>;
                        }

                        return (
                            <div className={classes.chips}>
                                {selected.map((selectedValueId) => {
                                    const value = selectedValues.find(
                                        selectedObject => selectedObject.id === selectedValueId,
                                    );
                                    return (
                                        <Chip
                                            icon={icon
                                        && React.cloneElement(
                                            icon,
                                            {
                                                style: {
                                                    color: getColor(
                                                        value.id,
                                                        1,
                                                        value.isDefault,
                                                    ),
                                                },
                                            },
                                        )}
                                            color="default"
                                            key={value.id}
                                            variant="outlined"
                                            label={value.text}
                                            onDelete={handleDelete(value.id)}
                                            className={classes.chip}
                                        />
                                    );
                                })
                                }
                            </div>
                        );
                    }
                    }
                    MenuProps={MenuProps}
                >
                    {allValues.map(value => (
                        <MenuItem key={value.id} value={value.id}>
                            <Checkbox color="primary" checked={isSelected(value.id)} />
                            <ListItemText primary={value.text} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    );
};

MultiSelect.propTypes = {
    allValues: graphKeysPropTypes.isRequired,
    selectedValues: graphKeysPropTypes.isRequired,
    handleSelect: PropTypes.func.isRequired,
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    icon: PropTypes.node,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    fullWidth: PropTypes.bool,
    labelBackgroundColor: PropTypes.string,
};

MultiSelect.defaultProps = {
    icon: null,
    placeholder: null,
    fullWidth: false,
    labelBackgroundColor: null,
};


export default withStyles(styles)(MultiSelect);
